import NStatusChip from "@/components/common/NStatusChip";
import useAuth from "@/lib/auth/useAuth";
import { formatDateStringMMDDYYYY } from "@/lib/utils/date";
import { getEnrollmentTierLabel } from "@/lib/utils/enrollment";
import { Field } from "@/types/field";
import {
    faChevronLeft,
    faPlus,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Card,
    CardFooter,
    CardHeader,
    Chip,
    Divider,
    Input,
    Skeleton,
} from "@heroui/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Key, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { sortArrayByKey } from "@/lib/utils/sorting";
import useLayout from "@/lib/layout/useLayout";
import useIsMobile from "@/lib/utils/useIsMobile";
import useRequest from "@/lib/request/useRequest";
import classNames from "classnames";
import useSessionPreferences from "@/lib/sessionPreferences/useSessionPreferences";
import { useTranslation } from "react-i18next";
import ApplyNChip from "@/components/common/ApplyNChip";
import DoNotApplyNChip from "@/components/common/DoNotApplyNChip";
export const Route = createFileRoute("/dashboard/fields/")({
    component: RouteComponent,
});
import { ViewType } from "@/types/enums";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { isNumber } from "@/lib/utils/numbers";

function FooterContent({ field }: { field: Field }) {
    const { t } = useTranslation("fields-list");
    const { user, userIsSentinel } = useAuth();
    const insightsEligible = useMemo(() => {
        return (
            userIsSentinel ||
            field.users.some(
                (u) => u.user_id === user?.id && u.view_type != ViewType.Trial,
            )
        );
    }, [field.users, user?.id, userIsSentinel]);

    if (!field.recommendation || !insightsEligible) return null;

    if (
        field.recommendation.user_recommendation != null &&
        field.recommendation.user_recommendation.recommend_application != null
    ) {
        return (
            <div className="flex gap-1">
                <p>
                    {field.recommendation.user_recommendation
                        .recommend_application ? (
                        <ApplyNChip />
                    ) : (
                        <DoNotApplyNChip />
                    )}
                </p>
                {field.recommendation.n_status && (
                    <NStatusChip nStatus={field.recommendation.n_status} />
                )}{" "}
            </div>
        );
    }

    if (!field.recommendation.insights_active) {
        return <div className="italic">{t("insights_inactive")}</div>;
    }

    if (field.recommendation.recent_application) {
        return (
            <div className="flex gap-1">
                <p>
                    {t("recent_application_on")}{" "}
                    {formatDateStringMMDDYYYY(
                        field.recommendation.recent_application,
                    )}
                </p>
            </div>
        );
    }
    if (field.recommendation.pending_application) {
        return <div>{t("pending_application")}</div>;
    }
    if (field.recommendation.gap) {
        return (
            <Chip
                classNames={{
                    base: "bg-gray-100",
                    content: "text-gray-900 font-bold",
                }}
                size="sm"
                radius="sm"
            >
                {t("image_gap")}
            </Chip>
        );
    }

    if (field.recommendation.recommend_fertigation) {
        return (
            <div className="flex gap-1">
                <Chip
                    classNames={{
                        base: "bg-red-100 font-bold",
                        content: "font-bold text-red-700",
                    }}
                    radius="sm"
                    size="sm"
                >
                    {t("apply_n")}
                </Chip>{" "}
                {field.recommendation.n_status && (
                    <NStatusChip nStatus={field.recommendation.n_status} />
                )}
            </div>
        );
    }

    return (
        <div className="flex gap-1">
            <Chip
                classNames={{
                    base: "bg-green-100 font-bold",
                    content: "font-bold text-green-700",
                }}
                radius="sm"
                size="sm"
            >
                {t("do_not_apply_n")}
            </Chip>
            {field.recommendation.n_status && (
                <NStatusChip nStatus={field.recommendation.n_status} />
            )}
        </div>
    );
}

function FieldListCard({
    field,
    isConsultantOrSentinel,
    userIsSentinel,
}: {
    field: Field;
    isConsultantOrSentinel: boolean;
    userIsSentinel: boolean;
}) {
    const { t } = useTranslation("fields-list");

    return (
        <Link
            to="/dashboard/fields/$fieldId/insights/ntime"
            params={{
                fieldId: field.id.toString(),
            }}
            search={{
                advisor: undefined,
            }}
        >
            <Card className="hover:bg-gray-200">
                <CardHeader className="pb-1">
                    <div className="flex w-full justify-between">
                        <div className="flex flex-col">
                            <p className="font-semibold text-lg">
                                {field.name}
                            </p>
                            {isConsultantOrSentinel && (
                                <p className="text-sm">{field.grower}</p>
                            )}
                        </div>
                        <Chip
                            classNames={{
                                base: classNames({
                                    "bg-brandNavy-500 text-white":
                                        !!field.active_enrollment,
                                    "bg-gray-600 text-white":
                                        !field.active_enrollment,
                                }),
                            }}
                            size="sm"
                            radius="sm"
                        >
                            {getEnrollmentTierLabel(
                                field.active_enrollment?.enrollment_tier,
                                t,
                            )}
                        </Chip>
                    </div>
                </CardHeader>
                <CardFooter className="text-xs pt-0">
                    <div className="flex flex-row justify-between w-full">
                        <FooterContent field={field} />
                        {userIsSentinel && (
                            <div>
                                {field.service_provider?.name ?? t("na")} (
                                {field.service_provider?.account_manager
                                    ?.full_name ?? t("na")}
                                )
                            </div>
                        )}
                    </div>
                </CardFooter>
            </Card>
        </Link>
    );
}

function RouteComponent() {
    const { t } = useTranslation("fields-list");
    const { fieldsFilter, setFieldsFilter } = useSessionPreferences();
    const { useGetRequest } = useRequest();

    const { setLayoutState } = useLayout();
    const { isMobile } = useIsMobile();
    useEffect(() => {
        setLayoutState("standard");
    }, [isMobile, setLayoutState]);

    const { isConsultantOrSentinel, userIsSentinel } = useAuth();
    const { data: fields, isLoading } = useGetRequest<
        (Field & { grower_id: number })[]
    >({
        url: "fields/",
    });
    const [search, setSearch] = useState(fieldsFilter.search ?? "");

    const [selectedAccountManager, setSelectedAccountManager] =
        useState<Key | null>(fieldsFilter.accountManager ?? null);
    const [selectedCSP, setSelectedCSP] = useState<Key | null>(
        fieldsFilter.csp ?? null,
    );
    const [selectedFarm, setSelectedFarm] = useState<Key | null>(
        fieldsFilter.farm ?? null,
    );

    useEffect(() => {
        setFieldsFilter({
            accountManager: selectedAccountManager as string | null,
            csp: selectedCSP as string | null,
            farm: selectedFarm as string | null,
            search: search,
        });
    }, [
        selectedAccountManager,
        selectedCSP,
        selectedFarm,
        search,
        setFieldsFilter,
    ]);

    const filteredFields = useMemo(() => {
        let filtered =
            fields?.filter((field) => {
                const lowerSearch = search.toLowerCase();
                return (
                    field.name.toLowerCase().includes(lowerSearch) ||
                    field.grower
                        .toString()
                        .toLowerCase()
                        .includes(lowerSearch) ||
                    field.service_provider?.name
                        .toLowerCase()
                        .includes(lowerSearch) ||
                    field.service_provider?.account_manager?.full_name
                        .toLowerCase()
                        .includes(lowerSearch)
                );
            }) ?? [];

        if (selectedAccountManager && isNumber(selectedAccountManager)) {
            filtered = filtered.filter(
                (field) =>
                    field.service_provider?.account_manager?.id ===
                    Number(selectedAccountManager),
            );
        }

        if (selectedCSP && isNumber(selectedCSP)) {
            filtered = filtered.filter(
                (field) => field.service_provider?.id === Number(selectedCSP),
            );
        }

        if (selectedFarm && isNumber(selectedFarm)) {
            filtered = filtered.filter(
                (field) => field.grower_id === Number(selectedFarm),
            );
        }

        return sortArrayByKey(filtered, "name");
    }, [fields, search, selectedAccountManager, selectedCSP, selectedFarm]);

    const parentRef = useRef<HTMLDivElement>(null);
    const virtualizer = useVirtualizer({
        count: filteredFields.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 100,
        overscan: 10,
    });

    const items = virtualizer.getVirtualItems();

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: isConsultantOrSentinel,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const serviceProviders = useMemo(() => {
        if (!userIsSentinel) return [];
        return sortArrayByKey(serviceProvidersQuery.data ?? [], "name");
    }, [serviceProvidersQuery.data, userIsSentinel]);

    const accountManagers = useMemo(() => {
        if (!userIsSentinel) return [];
        return sortArrayByKey(accountManagersQuery.data ?? [], "full_name");
    }, [accountManagersQuery.data, userIsSentinel]);

    const farms = useMemo(() => {
        if (!isConsultantOrSentinel) return [];
        return sortArrayByKey(growersQuery.data ?? [], "name");
    }, [growersQuery.data, isConsultantOrSentinel]);

    return (
        <div className="flex flex-col md:h-screen h-full">
            <div className="flex flex-row justify-between items-center gap-2 pr-2">
                <div className="flex flex-row gap-1 items-center p-2">
                    <Button
                        variant="light"
                        isIconOnly
                        size="lg"
                        as={Link}
                        href="/dashboard"
                        aria-label={t("back_to_dashboard")}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                    </Button>

                    <div className="font-bold text-3xl">{t("page_title")}</div>
                </div>
                <div>
                    <Button
                        as={Link}
                        to="/dashboard/fields/add"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                        color="primary"
                        size="sm"
                    >
                        {t("add_field")}
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 items-center gap-2 px-2 pb-2">
                <Input
                    placeholder={t("search_placeholder")}
                    defaultValue={fieldsFilter.search ?? ""}
                    onValueChange={setSearch}
                    isClearable
                    onClear={() => setSearch("")}
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    size="sm"
                />
                {userIsSentinel && (
                    <>
                        <Autocomplete
                            size="sm"
                            placeholder={t("account_manager_short")}
                            showScrollIndicators={false}
                            selectedKey={selectedAccountManager as string}
                            color={
                                selectedAccountManager ? "primary" : "default"
                            }
                            onSelectionChange={setSelectedAccountManager}
                            aria-label={t("account_manager")}
                        >
                            {accountManagers.map((accountManager) => {
                                return (
                                    <AutocompleteItem
                                        key={accountManager.id}
                                        textValue={accountManager.full_name}
                                    >
                                        {accountManager.full_name}
                                    </AutocompleteItem>
                                );
                            })}
                        </Autocomplete>
                        <Autocomplete
                            size="sm"
                            placeholder={t("csp")}
                            showScrollIndicators={false}
                            selectedKey={selectedCSP as string}
                            color={selectedCSP ? "primary" : "default"}
                            onSelectionChange={setSelectedCSP}
                            aria-label={t("csp")}
                        >
                            {serviceProviders.map((serviceProvider) => {
                                return (
                                    <AutocompleteItem
                                        key={serviceProvider.id}
                                        textValue={serviceProvider.name}
                                    >
                                        {serviceProvider.name}
                                    </AutocompleteItem>
                                );
                            })}
                        </Autocomplete>
                    </>
                )}
                {isConsultantOrSentinel && (
                    <Autocomplete
                        size="sm"
                        placeholder={t("farms")}
                        showScrollIndicators={false}
                        selectedKey={selectedFarm as string}
                        onSelectionChange={setSelectedFarm}
                        color={selectedFarm ? "primary" : "default"}
                        aria-label={t("farms")}
                    >
                        {farms.map((farm) => {
                            return (
                                <AutocompleteItem
                                    key={farm.id}
                                    textValue={farm.name}
                                >
                                    {farm.name}
                                </AutocompleteItem>
                            );
                        })}
                    </Autocomplete>
                )}
            </div>

            <Divider />
            {isLoading ? (
                <div className="h-full w-full p-2">
                    <Skeleton className="h-full w-full rounded-lg " />
                </div>
            ) : (
                <div
                    ref={parentRef}
                    style={{
                        height: "100%",
                        width: "100%",
                        overflowY: "scroll",
                        contain: "strict",
                    }}
                >
                    <div
                        style={{
                            height: virtualizer.getTotalSize(),
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${items[0]?.start ?? 0}px)`,
                            }}
                        >
                            {items.map((virtualRow) => (
                                <div
                                    key={virtualRow.key}
                                    data-index={virtualRow.index}
                                    ref={virtualizer.measureElement}
                                    className="p-1 pb-0"
                                >
                                    <FieldListCard
                                        userIsSentinel={userIsSentinel}
                                        field={filteredFields[virtualRow.index]}
                                        isConsultantOrSentinel={
                                            isConsultantOrSentinel
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
